import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/stores/auth';
import { usApi, useUsEnabled, usKeys } from './api';
import { syncMeMode } from './state';

/**
 * Us mode (SPEC_V15 §2). While it is on, the app *is* the space: every
 * signed-in route outside /us leads back to /us, and the rest of Popin's
 * interruptions (call invites from friends, nudges, ads, toasts about other
 * people) stay quiet until the person leaves the mode. Safety is never
 * quieted: moderation notices and the Safety Center work as everywhere.
 * A date is the one place the mode lets them go: opening its lobby or call
 * steps out of Us mode, like "Leave Us mode" does.
 */
export const isUsPath = (pathname: string) => pathname === '/us' || pathname.startsWith('/us/');

/** A date's lobby (`/dates/<invite>`) or its call (`/call/<session>`). */
export const isDatePath = (pathname: string) => /^\/(dates|call)\/[^/]+\/?$/.test(pathname);

/** True while the person is in their space (the Us routes). */
export function useInUs(): boolean {
  return isUsPath(useLocation().pathname);
}

/** Me says Us mode is on (and the flag is on). */
export function useUsModeOn(): boolean {
  const on = useAuth((s) => !!s.me?.us_mode);
  const enabled = useUsEnabled();
  return on && enabled;
}

/** Wraps signed-in routes: in Us mode, anywhere but /us (or a date) goes back to the space. */
export function UsModeGate({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const on = useUsModeOn();
  const qc = useQueryClient();
  const toDate = on && isDatePath(loc.pathname);
  // Going on a date leaves the space; otherwise the gate would pull them back mid-date.
  useEffect(() => {
    if (!toDate) return;
    usApi
      .setMode(false)
      .then((v) => {
        qc.setQueryData(usKeys.us, v);
        syncMeMode(v);
      })
      .catch(() => undefined);
  }, [toDate, qc]);
  if (on && !isUsPath(loc.pathname) && !toDate) return <Navigate to="/us" replace />;
  return <>{children}</>;
}

/** Renders children only outside the space (for global hosts that would interrupt it). */
export function OutsideUs({ children }: { children: ReactNode }) {
  return useInUs() ? null : <>{children}</>;
}
